import { createFileRoute, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Plus, Download, CheckCircle2, XCircle, Gavel } from 'lucide-react';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useOffer, useUpdateOffer } from '@/hooks/data/useOffers';
import { useProperty } from '@/hooks/data/useProperties';
import { useClient } from '@/hooks/data/useClients';
import { useLawyers } from '@/hooks/data/useLawyers';
import {
  parseAvailableLanguages,
  useCreateDocument,
  useDocumentsForOffer,
  type DocumentRow,
} from '@/hooks/data/useDocuments';
import { TranslateDocumentDialog } from '@/components/documents/TranslateDocumentDialog';
import { useAuth } from '@/hooks/useAuth';
import { apiFetch } from '@/lib/api';
import type { KaufanbotData } from '@/pdf/Kaufanbot';
import { formatDate, formatMoney, formatNumber } from '@/lib/formatLocale';

// PDF chunk is ~500KB gzipped (@react-pdf/renderer). Defer until first action.
const loadPdfModule = () => import('@/pdf/offerPdfGenerator');

export const Route = createFileRoute('/_app/offers_/$id')({
  component: OfferDetailPage,
});

function OfferDetailPage() {
  const { id } = useParams({ from: '/_app/offers_/$id' });
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: detail, isLoading } = useOffer(id);
  const updateOffer = useUpdateOffer(id);
  const createDoc = useCreateDocument();
  const { data: kaufanbotDocs = [] } = useDocumentsForOffer(id);
  const { profile } = useAuth();
  const [archiving, setArchiving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [archiveProgress, setArchiveProgress] = useState<{ done: number; total: number } | null>(null);
  const [translating, setTranslating] = useState<DocumentRow | null>(null);
  const [downloadingLang, setDownloadingLang] = useState<string | null>(null);
  // v2 — Annehmen / Ablehnen prominent actions + post-accept Notar callout.
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [notarOpen, setNotarOpen] = useState(false);

  const propertyId = detail?.offer.property_id;
  const { data: property } = useProperty(propertyId);
  const { data: lawyers = [] } = useLawyers(false);

  const buyerIds = detail?.participants.filter((p) => p.participant_role === 'buyer').map((p) => p.client_id) ?? [];
  const sellerIds = detail?.participants.filter((p) => p.participant_role === 'seller').map((p) => p.client_id) ?? [];

  if (isLoading) {
    return <p className="text-muted-foreground">{t('common.loading', 'Lädt …')}</p>;
  }
  if (!detail) {
    return <p className="text-destructive">{t('offers.detail.not_found', 'Kaufanbot nicht gefunden.')}</p>;
  }

  const offer = detail.offer;
  const lawyer = lawyers.find((l) => l.id === offer.lawyer_id);

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-ink">
            {t('offers.detail.title', 'Kaufanbot')}
          </h2>
          <p className="text-sm text-ink-3">
            {id.slice(0, 8)} · {t('common.created', 'Erstellt')}{' '}
            {fmtDate(offer.created_at)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge>
            {t(`enums.offer_status.${offer.status}`, offer.status)}
          </Badge>
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              // A click that throws inside an async handler is an unhandled
              // rejection: the button looks dead and nobody learns why.
              setDownloading(true);
              try {
                const { downloadKaufanbot } = await loadPdfModule();
                const data = await buildPdfData(detail, property, lawyer ?? null, profile?.full_name ?? 'Makler', buyerIds, sellerIds);
                await downloadKaufanbot(data, `Kaufanbot_${id.slice(0, 8)}.pdf`);
              } catch (err) {
                toast.error(
                  t('offers.actions.download_failed', 'PDF konnte nicht erzeugt werden: {{reason}}', {
                    reason: (err as Error).message,
                  }),
                );
              } finally {
                setDownloading(false);
              }
            }}
            disabled={!property || downloading}
          >
            {downloading && <Loader2 className="me-1.5 h-4 w-4 animate-spin" />}
            {t('offers.actions.download_pdf', 'PDF herunterladen')}
          </Button>
          <Button
            type="button"
            onClick={async () => {
              if (!property) return;
              setArchiving(true);
              setArchiveProgress({ done: 0, total: 10 });
              try {
                // 1) Reserve a documents row of type=kaufanbot linked to
                // the offer + first buyer client (so the chip strip can
                // surface the row through useDocumentsForOffer).
                const doc = await createDoc.mutateAsync({
                  type: 'kaufanbot',
                  offerId: id,
                  clientId: buyerIds[0] ?? null,
                  propertyId: property.id,
                });
                // 2) Build the PDF data using the existing helper.
                const data = await buildPdfData(
                  detail,
                  property,
                  lawyer ?? null,
                  profile?.full_name ?? 'Makler',
                  buyerIds,
                  sellerIds,
                );
                // 3) Render DE first (returns blob for instant local
                //    download), queue the other 9 in background.
                const { generateAllKaufanbot } = await loadPdfModule();
                const result = await generateAllKaufanbot(doc.id, data, {
                  onProgress: (_lang, done, total) => setArchiveProgress({ done, total }),
                  onError: (lang, err) => console.warn(`[kaufanbot] ${lang} failed`, err),
                });
                // 4) Trigger local DE download.
                const url = URL.createObjectURL(result.deBlob);
                const a = window.document.createElement('a');
                a.href = url;
                a.download = `Kaufanbot_${id.slice(0, 8)}.pdf`;
                window.document.body.appendChild(a);
                a.click();
                window.document.body.removeChild(a);
                URL.revokeObjectURL(url);
                // 5) Refresh chip strip when background queue settles.
                void result.backgroundPromise.finally(() => {
                  setArchiveProgress(null);
                  qc.invalidateQueries({ queryKey: ['documents', 'offer', id] });
                });
                toast.success(
                  `${t('offers.actions.archive_done', 'Kaufanbot archiviert')} · Nr. ${doc.number}`,
                );
              } catch (err) {
                toast.error((err as Error).message);
              } finally {
                setArchiving(false);
              }
            }}
            disabled={archiving || !property}
          >
            {archiving
              ? t('offers.actions.archiving', 'Wird archiviert …')
              : t('offers.actions.archive_pdf', 'PDF archivieren')}
          </Button>
          {archiveProgress && archiveProgress.done < archiveProgress.total && (
            <span className="inline-flex items-center gap-1 rounded-full border border-line bg-paper-2 px-2 py-0.5 text-[10px] font-medium text-ink-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              {t('docs.lang_progress', 'Sprache {{done}}/{{total}}', {
                done: archiveProgress.done,
                total: archiveProgress.total,
              })}
            </span>
          )}
        </div>
      </header>

      {/* v2 — prominent Annehmen / Ablehnen action band. Visible when the
          offer is signed or has been sent but not yet decided. Annehmen
          flips status to 'accepted' which auto-promotes the property to
          `kaufanbot_angenommen` server-side (see services/offers.ts). */}
      {(offer.status === 'sent' || offer.status === 'signed') && (
        <div className="rounded-md border border-line bg-card p-4 shadow-soft">
          {!rejecting ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] uppercase text-ink-3 tracking-[0.08em]">
                  {t('offers.detail.decision_eyebrow', 'Entscheidung')}
                </p>
                <p className="mt-0.5 text-sm text-ink-2">
                  {t(
                    'offers.detail.decision_hint',
                    'Anbot prüfen und annehmen oder ablehnen. Bei „Annehmen" wechselt die Immobilie automatisch zu „Kaufanbot angenommen".',
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={() => updateOffer.mutate({ status: 'accepted' })}
                  disabled={updateOffer.isPending}
                  className="bg-[hsl(var(--positive))] text-white hover:bg-[hsl(var(--positive))]/90"
                >
                  <CheckCircle2 className="me-1.5 h-4 w-4" />
                  {t('offers.detail.accept', 'Annehmen')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setRejectReason('');
                    setRejecting(true);
                  }}
                  disabled={updateOffer.isPending}
                  className="text-[hsl(var(--danger))]"
                >
                  <XCircle className="me-1.5 h-4 w-4" />
                  {t('offers.detail.reject', 'Ablehnen')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-ink">
                {t('offers.detail.reject_reason_label', 'Begründung der Ablehnung')}
              </p>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                placeholder={t(
                  'offers.detail.reject_reason_placeholder',
                  'Optional — z. B. Preis zu niedrig, Käufer hat zurückgezogen …',
                )}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setRejecting(false)}
                  disabled={updateOffer.isPending}
                >
                  {t('common.cancel', 'Abbrechen')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    updateOffer.mutate(
                      {
                        status: 'rejected',
                        rejectionReason: rejectReason.trim() || null,
                      },
                      {
                        onSuccess: () => {
                          setRejecting(false);
                          toast.success(t('offers.detail.reject_done', 'Anbot abgelehnt.'));
                        },
                      },
                    );
                  }}
                  disabled={updateOffer.isPending}
                  className="text-[hsl(var(--danger))]"
                >
                  <XCircle className="me-1.5 h-4 w-4" />
                  {t('offers.detail.reject_confirm', 'Ablehnung bestätigen')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* v2 — post-accept Notar call-out. Visible after the operator
          accepts the offer; opens CreateAppointmentDialog pre-filled
          with type=notar + buyer + property. */}
      {offer.status === 'accepted' && propertyId && buyerIds[0] && (
        <div className="rounded-md border border-line bg-card p-4 shadow-soft border-s-[3px] border-s-[hsl(var(--brand-gold))]">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-base text-ink">
                {t('offers.detail.next_step_title', 'Nächster Schritt: Notar-Termin')}
              </p>
              <p className="mt-0.5 text-sm text-ink-3">
                {t(
                  'offers.detail.next_step_body',
                  'Vereinbaren Sie jetzt einen Beurkundungstermin mit dem Käufer.',
                )}
              </p>
            </div>
            <Button type="button" onClick={() => setNotarOpen(true)}>
              <Gavel className="me-1.5 h-4 w-4" />
              {t('appointments.view.create_notar', 'Notar-Termin anlegen')}
            </Button>
          </div>
        </div>
      )}

      {/* Archived Kaufanbot documents — chip strip per language. Click DE
          downloads the master, click "+ N" opens TranslateDocumentDialog
          to add missing translations retroactively. */}
      {kaufanbotDocs.length > 0 && (
        <div className="space-y-2">
          {kaufanbotDocs.map((doc) => {
            const langs = parseAvailableLanguages(doc.available_languages);
            const order = ['de', 'en', 'fr', 'it', 'sr', 'bg', 'ru', 'tr', 'ar', 'zh'];
            const sorted = order.filter((l) => langs.includes(l));
            const missing = order.filter((l) => !langs.includes(l));
            return (
              <div
                key={doc.id}
                className="flex flex-wrap items-center gap-1 rounded-md border border-line bg-paper-2 px-2 py-1 text-[11px]"
              >
                <span className="font-medium text-ink">Kaufanbot</span>
                <span className="font-mono text-ink-3">Nr. {doc.number}</span>
                <span className="ms-auto inline-flex flex-wrap items-center gap-1">
                  <span className="text-[9px] uppercase tracking-wide text-ink-3">
                    {t('docs.langs.label', 'Sprachen')}
                  </span>
                  {sorted.map((lang) => {
                    const key = `${doc.id}:${lang}`;
                    const isLoading = downloadingLang === key;
                    const isDe = lang === 'de';
                    return (
                      <button
                        key={lang}
                        type="button"
                        onClick={async () => {
                          setDownloadingLang(key);
                          try {
                            const res = await apiFetch(
                              `/documents/${doc.id}/pdf?lang=${encodeURIComponent(lang)}`,
                            );
                            if (!res.ok) throw new Error(`download_failed_${res.status}`);
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = window.document.createElement('a');
                            a.href = url;
                            a.download = `Kaufanbot-${doc.number}-${lang}.pdf`;
                            window.document.body.appendChild(a);
                            a.click();
                            window.document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          } catch (e) {
                            toast.error((e as Error).message);
                          } finally {
                            setDownloadingLang(null);
                          }
                        }}
                        disabled={isLoading}
                        title={t(`docs.langs.${lang}`, lang.toUpperCase())}
                        className={
                          isDe
                            ? 'inline-flex items-center gap-1 rounded border border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/.08)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-ink hover:bg-[hsl(var(--brand-gold)/.16)] disabled:opacity-50'
                            : 'inline-flex items-center gap-1 rounded border border-line bg-card px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-ink-2 hover:bg-paper-3 disabled:opacity-50'
                        }
                      >
                        {isLoading ? (
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        ) : (
                          <Download className="h-2.5 w-2.5 opacity-60" />
                        )}
                        {lang.toUpperCase()}
                      </button>
                    );
                  })}
                  {missing.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTranslating(doc)}
                      title={t('docs.translate.add', 'Übersetzungen nachträglich hinzufügen')}
                      className="inline-flex items-center gap-1 rounded border border-dashed border-[hsl(var(--brand-gold))]/60 bg-[hsl(var(--brand-gold)/.04)] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-[hsl(var(--brand-gold-deep))] hover:bg-[hsl(var(--brand-gold)/.12)]"
                    >
                      <Plus className="h-2.5 w-2.5" />
                      {missing.length}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {translating && (
        <TranslateDocumentDialog
          doc={translating}
          missingLangs={['de', 'en', 'fr', 'it', 'sr', 'bg', 'ru', 'tr', 'ar', 'zh'].filter(
            (l) => !parseAvailableLanguages(translating.available_languages).includes(l),
          )}
          open={true}
          onOpenChange={(o) => !o && setTranslating(null)}
        />
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('offers.detail.summary', 'Zusammenfassung')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Field
              label="Kaufpreis"
              value={formatMoney((offer.offered_price / 100), { maximumFractionDigits: 2 })}
            />
            <Field label={t('offers.validUntil', 'Gültig bis')} value={fmtDate(offer.expires_at)} />
            <Field label="USt anwendbar" value={offer.vat_applicable ? 'Ja' : 'Nein'} />
            <Field
              label="Finanzierung"
              value={
                offer.financing_type === 'with_reservation'
                  ? `Mit Vorbehalt (Frist: ${fmtDate(offer.financing_deadline)})`
                  : offer.financing_type === 'without_reservation'
                    ? 'Ohne Vorbehalt'
                    : '—'
              }
            />
            <Field label="Anwalt" value={lawyer?.full_name ?? lawyer?.last_name ?? '—'} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('offers.detail.actions', 'Aktionen')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {(['draft', 'sent', 'accepted', 'rejected', 'expired'] as const).map((s) => (
                <Button
                  key={s}
                  variant={offer.status === s ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => updateOffer.mutate({ status: s })}
                  disabled={updateOffer.isPending}
                >
                  {t(`enums.offer_status.${s}`, s)}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {notarOpen && propertyId && buyerIds[0] && (
          <CreateAppointmentDialog
            open={notarOpen}
            onOpenChange={setNotarOpen}
            defaultType="notar"
            defaultClientId={buyerIds[0]}
            defaultPropertyId={propertyId}
          />
        )}

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>{t('offers.detail.attachments', 'Anhänge')}</CardTitle>
          </CardHeader>
          <CardContent>
            {detail.attachments.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {t('offers.detail.no_attachments', 'Noch keine PDFs archiviert.')}
              </p>
            )}
            <ul className="space-y-1 text-sm">
              {detail.attachments.map((a) => (
                <li key={a.id} className="flex items-center justify-between rounded-md border border-border/50 px-3 py-2">
                  <span>{a.file_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {a.file_size ? `${Math.round(a.file_size / 1024)} KB` : ''} · {fmtDate(a.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-border/40 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-end">{value}</span>
    </div>
  );
}

// Defensive date formatter — timestamps come from the API as ISO strings (not
// numbers, despite the *Row type declaring `number`). Returns '—' for
// null/undefined/invalid input.
function fmtDate(value: number | string | null | undefined): string {
  if (value == null) return '—';
  const d = typeof value === 'number' ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return formatDate(d);
}

async function buildPdfData(
  detail: { offer: { offered_price: number; expires_at: number; vat_applicable: boolean; lawyer_fee_percentage: number | null; buyer_commission_type: 'percent' | 'fixed' | null; buyer_commission_value: number | null; seller_commission_type: 'percent' | 'fixed' | null; seller_commission_value: number | null; financing_type: 'without_reservation' | 'with_reservation' | null; financing_deadline: number | null; viewing_date: number | null; signing_place: string | null; special_agreements: string | null } },
  property: KaufanbotData['property'] | undefined,
  lawyer: KaufanbotData['lawyer'],
  agentName: string,
  buyerIds: string[],
  sellerIds: string[],
): Promise<KaufanbotData> {
  // Fetch full client data (sequential to keep dialog code linear)
  const buyers: KaufanbotData['buyers'] = await Promise.all(buyerIds.map(loadClientForPdf));
  const sellers: KaufanbotData['sellers'] = await Promise.all(sellerIds.map(loadClientForPdf));

  return {
    offer: detail.offer,
    property: property ?? {
      address: null,
      city: '',
      postal_code: null,
      edi_real_number: null,
      object_type: null,
      property_size: null,
      kg_grundbuch: null,
      einlagezahl: null,
      b_laufnummer: null,
    },
    lawyer,
    agentName,
    buyers,
    sellers,
  };
}

async function loadClientForPdf(id: string): Promise<KaufanbotData['buyers'][number]> {
  const { apiFetch } = await import('@/lib/api');
  const res = await apiFetch(`/clients/${id}`);
  if (!res.ok) {
    return {
      title: null,
      first_name: null,
      last_name: id,
      address: null,
      birth_date: null,
      citizenship: null,
      sv_number: null,
      phone: '',
      email: null,
    };
  }
  const c = (await res.json()) as {
    title: string | null;
    first_name: string | null;
    last_name: string;
    phone: string;
    email: string | null;
    address: string | null;
    birth_date: string | null;
    citizenship: string | null;
    sv_number: string | null;
  };
  return {
    title: c.title,
    first_name: c.first_name,
    last_name: c.last_name,
    address: c.address,
    birth_date: c.birth_date,
    citizenship: c.citizenship,
    sv_number: c.sv_number,
    phone: c.phone,
    email: c.email,
  };
}

void useClient; // kept imported for future use; lint-safe.
